import { observer } from 'mobx-react-lite'
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'

import { PageHeader, Search } from '@/shared'
import { faqState } from '@App/store'
import { type ISwapSequencs } from '@App/types/common'
import { type IAddFaqDto, type IFaqData } from '@App/types/faqTypes'
import { onUseSearchDebounce } from '@App/utils'

import { AddFaqModal } from './AddFaqModal/AddFaqModal'
import { Control } from './Control/Control'
import style from './Faq.module.scss'
import { FaqListWrapper } from './FaqList/FaqListWrapper'

export const Faq = observer(() => {
    const ref = useRef<HTMLInputElement | null>(null)

    const [showAddItem, setShowAddItem] = useState(false)

    const onCLoseAddItem = () => setShowAddItem(false)
    const onOpenAddItem = () => setShowAddItem(true)

    const onGetFaqData = useCallback(() => {
        if (!faqState.faqList) {
            faqState.onGetFaqData()
        }
        return () => {
            faqState.onSetFaqData(null)
        }
    }, [])

    const onGetUnchangedFaqItemById = useCallback(({ id }: { id: string }) => {
        faqState.onGetUnchangedFaqItemById({ id })
    }, [])

    const onDebouncesSearch = onUseSearchDebounce((search) => {
        faqState.onSetFilters({ ...faqState.filters, search })
        faqState.onGetFaqData()
    }, 500)

    const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
        faqState.onSetFaqData(null)
        onDebouncesSearch(e.target.value)
    }

    const swapBySequence = useCallback((dto: ISwapSequencs) => {
        faqState.onSwapBySequence({ dto })
    }, [])

    useEffect(onGetFaqData, [onGetFaqData])

    const onGetPageItemsQuantity = (quantity: number) => {
        const cases = [2, 0, 1, 1, 1, 2]
        const titles = ['вопрос', 'вопроса', 'вопросов']
        if (quantity % 100 > 4 && quantity % 100 < 20) {
            return `${quantity} ${titles[2]}`
        } else {
            return `${quantity} ${titles[cases[Math.min(quantity % 10, 5)]]}`
        }
    }

    const pageInfo = onGetPageItemsQuantity(faqState?.faqList?.length || 0)

    const updateFaqList = useCallback((dto: IFaqData) => faqState.onUpdateFaqList(dto), [])
    const removeFaqById = useCallback((faqId: string) => faqState.onRemoveFaqById(faqId), [])
    const postFaq = useCallback((dto: IAddFaqDto) => faqState.onPostFaq(dto), [])

    return (
        <div className={style.container}>
            {faqState.faqList && showAddItem && (
                <AddFaqModal show={showAddItem} onClose={onCLoseAddItem} postFaq={postFaq} count={faqState.count} />
            )}
            <PageHeader pageName={'FAQ'} pageInfo={pageInfo} />
            <div className={style.searchWrapper}>
                <Search ref={ref} onSearch={onSearch} placeholder='Поиск по FAQ' />
            </div>
            <Control onAddFaq={onOpenAddItem} />
            <FaqListWrapper
                setFaqEditableState={faqState.onSetFaqData}
                list={faqState.faqList}
                onSwapBySequence={swapBySequence}
                onGetUnchangedFaqItemById={onGetUnchangedFaqItemById}
                updateFaqList={updateFaqList}
                removeFaqById={removeFaqById}
            />
        </div>
    )
})
